/**
 * Game side handle on the bug reporter.
 *
 * The reporter lives in its own bundle that loads first, so from the game's
 * point of view it is an optional global: every call here is a no-op if the
 * reporter bundle is missing (a stripped build, a load failure) rather than an
 * error the game has to handle.
 */

import type { ReportOptions, SubmitResult } from './submitReport';

export type BugReporterApi = {
	registerContextProvider: (fn: () => Record<string, any>) => void;
	registerServerWorker: (worker: Worker) => void;
	submitReport: (options: ReportOptions) => Promise<SubmitResult>;
	captureScreenshot: () => Promise<Uint8Array | null>;
	setButtonFlashing: (flash: boolean) => void;
	getButtonRect: () => DOMRect | null;
};

function api(): Partial<BugReporterApi> | undefined {
	return (window as any)?.__bugReporter;
}

/** Hands the reporter whatever game state is worth attaching to a report. */
export function registerBugReportContextProvider(fn: () => Record<string, any>) {
	try {
		api()?.registerContextProvider?.(fn);
	} catch (e) {
		console.warn('Could not register bug report context provider', e);
	}
}

/** Lets the reporter pull logs out of the standalone server worker. */
export function registerBugReportServerWorker(worker: Worker) {
	try {
		api()?.registerServerWorker?.(worker);
	} catch (e) {
		console.warn('Could not register bug report server worker', e);
	}
}

/**
 * Sends a report through the reporter bundle, rather than this bundle's own copy of the code,
 * since that is where the context provider and server worker are registered.
 */
export async function submitBugReport(options: ReportOptions): Promise<SubmitResult> {
	const submit = api()?.submitReport;
	if (!submit) return { ok: false, message: 'The bug reporter is not available.' };
	try {
		return await submit(options);
	} catch (e: any) {
		return { ok: false, message: `Could not send the report: ${e?.message ?? e}` };
	}
}

/** Null if this build can't take screenshots, or the reporter is missing */
export async function captureBugReportScreenshot(): Promise<Uint8Array | null> {
	try {
		return await api()?.captureScreenshot?.() ?? null;
	} catch (e) {
		console.warn('Could not capture bug report screenshot', e);
		return null;
	}
}

/** Pulses the report button to draw attention to it, eg from the tutorial. Stays on until turned off */
export function setBugReportButtonFlashing(flash: boolean) {
	try {
		api()?.setButtonFlashing?.(flash);
	} catch (e) {
		console.warn('Could not set bug report button flashing', e);
	}
}

/** Where the report button is on screen. Null if the reporter or its button is missing */
export function getBugReportButtonRect(): DOMRect | null {
	try {
		return api()?.getButtonRect?.() ?? null;
	} catch (e) {
		console.warn('Could not get bug report button position', e);
		return null;
	}
}
